import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';
import { AudioConfig, ResultReason, SpeechConfig, SpeechRecognizer, SpeechTranslationConfig, TranslationRecognizer, } from 'microsoft-cognitiveservices-speech-sdk';
import { DataMessage, DataMsgBodyType, DataMsgType, } from '../../../helpers/proto/plugnmeet_datamessage_pb';
import { sendWebsocketMessage } from '../../../helpers/websocket';
import { AzureTokenRenewReq, GenerateAzureTokenReq, SpeechServiceUserStatusReq, SpeechServiceUserStatusTasks, } from '../../../helpers/proto/plugnmeet_speech_services_pb';
import sendAPIRequest from '../../../helpers/api/plugNmeetAPI';
import { CommonResponse } from '../../../helpers/proto/plugnmeet_common_api_pb';
import { store } from '../../../store';
import i18n from '../../../helpers/i18n';
import { supportedSpeechToTextLangs } from './supportedLangs';
let session = undefined;
const getSession = () => {
    return store.getState().session;
};
export const openConnectionWithAzure = (azureInfo, mediaStream, speechLang, speechService, setOptionSelectionDisabled, setRecognizer, unsetRecognizer) => {
    var _a, _b;
    let audioConfig, speechConfig, recognizer;
    if (mediaStream) {
        audioConfig = AudioConfig.fromStreamInput(mediaStream);
    }
    else {
        toast(i18n.t('speech-services.mic-error'), {
            type: 'error',
        });
        return;
    }
    const sl = supportedSpeechToTextLangs.filter((l) => l.code === speechLang)[0];
    let transLangs = [];
    if (speechService.is_enabled_translation) {
        if ((_a = speechService.allowed_trans_langs) === null || _a === void 0 ? void 0 : _a.length) {
            transLangs = speechService.allowed_trans_langs.filter((l) => l !== sl.locale);
        }
        (_b = speechService.allowed_speech_langs) === null || _b === void 0 ? void 0 : _b.filter((l) => l !== sl.code).forEach((s) => {
            const speechObj = supportedSpeechToTextLangs.filter((l) => l.code === s);
            if (speechObj[0].locale === sl.locale) {
                // same locale, so we can avoid to add
                return;
            }
            const hasLang = transLangs.find((l) => l === speechObj[0].locale);
            if (!hasLang) {
                transLangs.push(speechObj[0].locale);
            }
        });
    }
    if (transLangs.length) {
        speechConfig = SpeechTranslationConfig.fromAuthorizationToken(azureInfo.token, azureInfo.serviceRegion);
        transLangs.forEach((l) => speechConfig.addTargetLanguage(l));
    }
    else {
        speechConfig = SpeechConfig.fromAuthorizationToken(azureInfo.token, azureInfo.serviceRegion);
    }
    speechConfig.speechRecognitionLanguage = speechLang;
    speechConfig.enableDictation();
    if (transLangs.length) {
        recognizer = new TranslationRecognizer(speechConfig, audioConfig);
    }
    else {
        recognizer = new SpeechRecognizer(speechConfig, audioConfig);
    }
    recognizer.sessionStarted = async () => {
        setOptionSelectionDisabled(false);
        const res = await sendUserSessionStatus(SpeechServiceUserStatusTasks.SPEECH_TO_TEXT_SESSION_STARTED, azureInfo.keyId);
        if (!res.status) {
            toast(i18n.t('speech-services.status-change-error', { error: res.msg }), {
                type: 'error',
            });
            if (recognizer) {
                unsetRecognizer();
            }
        }
        else {
            toast(i18n.t('speech-services.speech-to-text-ready'), {
                type: 'success',
                autoClose: 3000,
            });
        }
    };
    recognizer.sessionStopped = async () => {
        await sendUserSessionStatus(SpeechServiceUserStatusTasks.SPEECH_TO_TEXT_SESSION_ENDED, azureInfo.keyId);
        toast(i18n.t('speech-services.speech-to-text-stopped'), {
            type: 'success',
            autoClose: 3000,
        });
    };
    recognizer.recognizing = (sender, recognitionEventArgs) => {
        var _a, _b;
        const result = recognitionEventArgs.result;
        const data = {
            type: 'interim',
            from: (_b = (_a = session === null || session === void 0 ? void 0 : session.currentUser) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '',
            result: {
                [sl.locale]: result.text,
            },
        };
        if (result.reason === ResultReason.TranslatingSpeech) {
            if (speechService) {
                transLangs.forEach((l) => {
                    const text = result.translations.get(l);
                    if (!isEmpty(text)) {
                        data.result[l] = text;
                    }
                });
            }
        }
        broadcastSpeechToTextMsgs(data);
    };
    recognizer.recognized = (sender, recognitionEventArgs) => {
        var _a, _b;
        const result = recognitionEventArgs.result;
        const data = {
            type: 'final',
            from: (_b = (_a = session === null || session === void 0 ? void 0 : session.currentUser) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '',
            result: {
                [sl.locale]: result.text,
            },
        };
        if (result.reason === ResultReason.TranslatedSpeech) {
            if (speechService) {
                transLangs.forEach((l) => {
                    const text = result.translations.get(l);
                    if (!isEmpty(text)) {
                        data.result[l] = text;
                    }
                });
            }
        }
        broadcastSpeechToTextMsgs(data);
    };
    recognizer.canceled = async (s, e) => {
        setOptionSelectionDisabled(false);
        await sendUserSessionStatus(SpeechServiceUserStatusTasks.SPEECH_TO_TEXT_SESSION_ENDED, azureInfo.keyId);
        toast(i18n.t('speech-services.azure-error', {
            error: e.errorCode + ': ' + e.errorDetails,
        }), {
            type: 'error',
        });
        unsetRecognizer();
    };
    recognizer.startContinuousRecognitionAsync();
    setRecognizer(recognizer);
};
export const broadcastSpeechToTextMsgs = (msg) => {
    var _a, _b, _c, _d;
    if (!session) {
        session = getSession();
    }
    const dataMsg = new DataMessage({
        type: DataMsgType.SYSTEM,
        roomSid: session.currentRoom.sid,
        roomId: session.currentRoom.room_id,
        body: {
            type: DataMsgBodyType.SPEECH_SUBTITLE_TEXT,
            from: {
                sid: (_b = (_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.sid) !== null && _b !== void 0 ? _b : '',
                userId: (_d = (_c = session.currentUser) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : '',
            },
            msg: JSON.stringify(msg),
        },
    });
    sendWebsocketMessage(dataMsg.toBinary());
};
export const getAzureToken = async () => {
    var _a;
    if (!session) {
        session = getSession();
    }
    const body = new GenerateAzureTokenReq({
        userSid: (_a = session === null || session === void 0 ? void 0 : session.currentUser) === null || _a === void 0 ? void 0 : _a.sid,
    });
    const r = await sendAPIRequest('speechServices/azureToken', body.toBinary(), false, 'application/protobuf', 'arraybuffer');
    return CommonResponse.fromBinary(new Uint8Array(r));
};
export const renewAzureToken = async () => {
    var _a;
    if (!session) {
        session = getSession();
    }
    const azureTokenInfo = store.getState().roomSettings.azureTokenInfo;
    if (!azureTokenInfo) {
        return;
    }
    const body = new AzureTokenRenewReq({
        userSid: (_a = session === null || session === void 0 ? void 0 : session.currentUser) === null || _a === void 0 ? void 0 : _a.sid,
        serviceRegion: azureTokenInfo.serviceRegion,
        keyId: azureTokenInfo.keyId,
    });
    const r = await sendAPIRequest('speechServices/renewToken', body.toBinary(), false, 'application/protobuf', 'arraybuffer');
    return CommonResponse.fromBinary(new Uint8Array(r));
};
export const sendUserSessionStatus = async (task, keyId) => {
    var _a;
    if (!session) {
        session = getSession();
    }
    const userId = (_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.userId;
    const body = new SpeechServiceUserStatusReq({
        task,
        userId,
        keyId,
        roomSid: session === null || session === void 0 ? void 0 : session.currentRoom.sid,
    });
    const r = await sendAPIRequest('speechServices/userStatus', body.toBinary(), false, 'application/protobuf', 'arraybuffer');
    return CommonResponse.fromBinary(new Uint8Array(r));
};
export const enableOrDisableSpeechService = async (body) => {
    const r = await sendAPIRequest('speechServices/serviceStatus', body.toBinary(), false, 'application/protobuf', 'arraybuffer');
    return CommonResponse.fromBinary(new Uint8Array(r));
};
